import {
  Box,
  Button,
  DismissIcon,
  Flex,
  Hide,
  Link,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { IconComponentProps, MarginProperties } from '@iheartjane/reefer';
import { useEffect, useRef, useState } from 'react';

import type { ActiveFilters as ActiveFilterType } from '@jane/search/types';

import { StyledActiveFilterWrapper } from './productFilterBar.styles';
import type { ProductFilterBarProps } from './productFilterBar.types';

function getFilterLabel(filter: ActiveFilterType) {
  return filter.key === 'has_brand_discount' ? 'Jane Gold' : filter.label;
}

interface ActiveFilterProps extends Pick<
  ProductFilterBarProps,
  'activeFilters' | 'noPadding' | 'onChange' | 'onDeselect'
> {
  isMenu?: boolean;
}

export const ActiveFilters = ({
  activeFilters,
  isMenu,
  noPadding,
  onChange,
  onDeselect,
}: ActiveFilterProps) => {
  const isMobile = useMobileMediaQuery({});
  const [announcement, setAnnouncement] = useState('');
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingFocusIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!announcement) return;
    const timer = setTimeout(() => setAnnouncement(''), 1000);
    return () => clearTimeout(timer);
  }, [announcement]);

  useEffect(() => {
    if (pendingFocusIndexRef.current === null) return;
    const removedIndex = pendingFocusIndexRef.current;
    pendingFocusIndexRef.current = null;
    const buttons =
      filterContainerRef.current?.querySelectorAll<HTMLButtonElement>(
        'button'
      ) ?? [];
    if (buttons.length > 0) {
      buttons[Math.min(removedIndex, buttons.length - 1)]?.focus();
    }
  }, [activeFilters]);

  const handleRemoveFilter: ProductFilterBarProps['onChange'] = (
    key,
    value
  ) => {
    const removedIndex = activeFilters.findIndex(
      (f) => f.key === key && f.value === value
    );
    if (removedIndex >= 0) {
      const removed = activeFilters[removedIndex];
      setAnnouncement(`Removed ${getFilterLabel(removed)} filter`);
      pendingFocusIndexRef.current = removedIndex;
    }
    onChange(key, value);
  };

  const handleClearAll = () => {
    const labels = activeFilters.map(getFilterLabel);
    setAnnouncement(`Removed ${labels.join(', ')} filters`);
    onDeselect('all');
  };

  const filterButtons = activeFilters.map((filter) => (
    <Box key={filter.label}>
      <ActiveFilterButton
        activeFilter={filter}
        onChange={handleRemoveFilter}
        mr={16}
        mb={16}
      />
    </Box>
  ));

  return (
    <>
      <Hide aria-live="polite" isHidden>
        {announcement}
      </Hide>
      {activeFilters.length > 0 &&
        (isMobile ? (
          <Box>
            <StyledActiveFilterWrapper ref={filterContainerRef}>
              {filterButtons}
            </StyledActiveFilterWrapper>
            <Link onClick={handleClearAll} ml={24} mb={16} typography="body">
              Clear all
            </Link>
          </Box>
        ) : (
          <Flex
            ref={filterContainerRef}
            alignItems="center"
            flexWrap="wrap"
            px={noPadding ? 0 : isMenu ? 64 : 24}
          >
            {filterButtons}
            <Link onClick={handleClearAll} ml={12} mb={16} typography="body">
              Clear all
            </Link>
          </Flex>
        ))}
    </>
  );
};

const ActiveFilterButton = ({
  activeFilter,
  onChange,
  ...margins
}: {
  activeFilter: ActiveFilterType;
  onChange: ProductFilterBarProps['onChange'];
} & MarginProperties) => {
  const { icon, key, value } = activeFilter;
  const Icon = icon as IconComponentProps;
  const buttonLabel = getFilterLabel(activeFilter);

  return (
    <Button
      key={buttonLabel}
      label={buttonLabel}
      aria-label={`Clear ${buttonLabel} filter`}
      endIcon={icon ? <Icon size="sm" aria-hidden /> : undefined}
      startIcon={<DismissIcon aria-hidden />}
      onClick={() => onChange(key, value)}
      variant="secondary"
      {...margins}
    />
  );
};
